import { API_BASE } from './apiBase';

/**
 * 官网公开内容的读取端（技术文章与待上新产品）。
 *
 * 内容由 ADMIN 后台维护，经 CONSOLE 的公开只读接口下发。
 * 失败时抛错，由页面决定是显示错误还是回落到静态内容。
 */

export interface ArticleSummary {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  tags: string[];
  publishedAt: string | null;
  minutes: number;
}

export interface Article extends ArticleSummary {
  body: string;
}

export interface UpcomingProduct {
  id: string;
  code: string;
  name: string;
  tagline: string;
  note: string;
  icon: string;
}

export class ContentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ContentError';
  }
}

async function readData<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => null)) as
    | { ok?: boolean; data?: T; error?: { message?: unknown } }
    | null;
  if (!response.ok || body?.ok !== true) {
    const message =
      typeof body?.error?.message === 'string' && body.error.message !== ''
        ? body.error.message
        : `内容加载失败（HTTP ${response.status}）`;
    throw new ContentError(message);
  }
  return body.data as T;
}

export async function fetchArticles(signal?: AbortSignal): Promise<ArticleSummary[]> {
  const response = await fetch(`${API_BASE}/api/content/articles`, { signal });
  const data = await readData<{ items: ArticleSummary[] }>(response);
  return data.items ?? [];
}

export async function fetchArticle(slug: string, signal?: AbortSignal): Promise<Article> {
  const response = await fetch(`${API_BASE}/api/content/articles/${encodeURIComponent(slug)}`, {
    signal,
  });
  const data = await readData<{ article: Article }>(response);
  return data.article;
}

export async function fetchUpcoming(signal?: AbortSignal): Promise<UpcomingProduct[]> {
  const response = await fetch(`${API_BASE}/api/content/upcoming`, { signal });
  const data = await readData<{ items: UpcomingProduct[] }>(response);
  return data.items ?? [];
}
